import React, { useEffect, useState, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import "dayjs/locale/es"; // Importar locale para DatePicker
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { descargarExcelCronograma as descargarExcel } from "../services/excel.service.js";
import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import {
  Backdrop,
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  IconButton,
  Button,
  Grid,
  Modal,
  Fade,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Tooltip,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
} from "@mui/material";
import BurbujasLoader from "./UIElements/BurbujasLoader";
import BlockIcon from "@mui/icons-material/Block";

import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import SearchIcon from "@mui/icons-material/Search";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import BotonCircular from "./UIElements/BotonCircular.jsx";
import Titulo from "../components/fonts/TituloPrincipal.jsx";
import { getInstancias } from "../services/instancias.service.js";
import ExcelDownloadModal from "./Cronograma/Modals/ExcelDownloadModal.jsx";
import CronogramaCalendar from "./Cronograma/CronogramaCalendar.jsx";

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.locale("es");

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxWidth: 700,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
};

const MONTH_NAMES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];
const DATE_FORMATS_TO_TRY = [
  "DD/MM/YYYY",
  "D/M/YYYY",
  "DD-MM-YYYY",
  "D-M-YYYY",
  "YYYY-MM-DD",
];

const parseDate = (dateString) => {
  if (!dateString) return null;
  const trimmedDateStr = dateString.trim();
  for (const format of DATE_FORMATS_TO_TRY) {
    const d = dayjs(trimmedDateStr, format, true);
    if (d.isValid()) return d;
  }
  return null;
};

const formatBooleanToSiNo = (value) => {
  if (value === true || value === 1) return "Sí";
  if (value === false || value === 0) return "No";
  return "N/A";
};

// --- ESTILOS PARA LA MAQUETACIÓN DE GRID ---
const gridContainerStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  padding: "20px",
  width: "100%",
  boxSizing: "border-box",
  minHeight: "100vh",
};

const Cronograma = ({ user }) => {
  const [cursosData, setCursosData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [ministerioOptions, setMinisterioOptions] = useState([]);
  const [areaOptions, setAreaOptions] = useState(["all"]);
  const [ministerioFilter, setMinisterioFilter] = useState("all");
  const [areaFilter, setAreaFilter] = useState("all");
  const [nombreFilter, setNombreFilter] = useState("");
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [monthFilter, setMonthFilter] = useState("all");
  const [activosFilterActive, setActivosFilterActive] = useState(false);
  const [omitirCancelados, setOmitirCancelados] = useState(false);
  const [excelModalOpen, setExcelModalOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const isREF = user?.rol === "REF";

  const availableYears = useMemo(() => {
    if (!cursosData.length) return [new Date().getFullYear()];
    const yearsSet = new Set();
    cursosData.forEach((c) => {
      const d = parseDate(c["Fecha inicio del curso"]);
      if (d) yearsSet.add(d.year());
    });
    const years = Array.from(yearsSet).sort((a, b) => a - b);
    // Asegurar que el año actual esté siempre presente
    const currentYear = new Date().getFullYear();
    if (!years.includes(currentYear)) years.push(currentYear);
    return years.sort((a, b) => a - b);
  }, [cursosData]);

  const COLUMNAS_VISIBLES = useMemo(
    () => [
      "Ministerio",
      "Area",
      "Nombre del curso",
      "Fecha inicio de inscripción",
      "Fecha fin de inscripción",
      "Fecha inicio del curso",
      "Fecha fin del curso",
      "Estado de Instancia",
    ],
    [],
  );

  const columnsForGrid = useMemo(() => {
    return COLUMNAS_VISIBLES.map((headerKey) => {
      let flex = 1;
      let minWidth = 130;
      let valueFormatter = undefined;

      if (headerKey === "Nombre del curso") {
        flex = 2.5;
        minWidth = 250;
      }
      if (headerKey === "Ministerio" || headerKey === "Area") {
        flex = 1.5;
        minWidth = 150;
      }
      if (headerKey.toLowerCase().includes("fecha")) {
        flex = 1.2;
        minWidth = 140;
        valueFormatter = (value) => {
          if (!value) return "";
          const d = dayjs(value);
          return d.isValid() ? d.format("DD/MM/YYYY") : value;
        };
      }
      return {
        field: headerKey,
        headerName: headerKey,
        flex,
        minWidth,
        valueFormatter,
      };
    });
  }, [COLUMNAS_VISIBLES]);

  const formatValue = useCallback(
    (value) => (value == null ? "" : String(value).trim()),
    [],
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const rawDataCronograma = await getInstancias();
        if (!Array.isArray(rawDataCronograma))
          throw new Error("Los datos recibidos no son válidos.");

        const minSet = new Set();
        const dataObjs = rawDataCronograma.map((instance, idx) => {
          const detalle = instance.detalle_curso || {};
          const areaDetalle = detalle.detalle_area || {};
          const ministerioDetalle = areaDetalle.detalle_ministerio || {};

          const obj = {
            id: `${instance.curso}-${instance.fecha_inicio_curso}-${idx}`, // ID más robusto
            "Código del curso": formatValue(instance.curso || detalle.cod),
            "Nombre del curso": formatValue(detalle.nombre),
            Ministerio: formatValue(ministerioDetalle.nombre),
            Area: formatValue(areaDetalle.nombre),
            "Fecha inicio del curso": formatValue(instance.fecha_inicio_curso),
            "Fecha fin del curso": formatValue(instance.fecha_fin_curso),
            "Fecha inicio de inscripción": formatValue(
              instance.fecha_inicio_inscripcion,
            ),
            "Fecha fin de inscripción": formatValue(
              instance.fecha_fin_inscripcion,
            ),
            Cupo: formatValue(instance.cupo),
            "Cantidad de horas": formatValue(instance.cantidad_horas),
            "Publica PCC":
              instance.es_publicada_portal_cc ?? detalle.publica_pcc,
            "Es Autogestionado":
              instance.es_autogestionado ?? detalle.es_autogestionado,
            "Estado de Instancia": formatValue(instance.estado_instancia),
            "Medio de inscripción": formatValue(instance.medio_inscripcion),
            "Plataforma de dictado": formatValue(instance.plataforma_dictado),
            "Tipo de capacitación": formatValue(instance.tipo_capacitacion),
            Comentario: formatValue(instance.comentario),
            "Datos de solicitud": formatValue(instance.datos_solictud),
            "Cantidad de inscriptos": instance.cantidad_inscriptos || 0,
            "Restricciones por Departamento":
              instance.detalle_restricciones_por_departamento || [],
            "Restricciones por Correlatividad":
              instance.detalle_restricciones_por_correlatividad || [],
            "Tiene restricción por edad":
              instance.tiene_restriccion_edad ?? detalle.tiene_restriccion_edad,
            "Edad Desde": instance.restriccion_edad_desde,
            "Edad Desde": instance.restriccion_edad_desde,
            "Edad Hasta": instance.restriccion_edad_hasta,
            _raw: instance, // Guardamos la instancia original para el export
          };

          if (obj["Ministerio"]) minSet.add(obj["Ministerio"]);
          return obj;
        });

        setCursosData(dataObjs);
        setMinisterioOptions(["all", ...Array.from(minSet).sort((a, b) => a.localeCompare(b))]);
      } catch (err) {
        console.error("Error al cargar datos:", err);
        setError(err.message || "Error al cargar datos.");
      } finally {
        setLoading(false);
      }
    })();
  }, [formatValue]);

  useEffect(() => {
    if (loading || !cursosData.length) {
      setAreaOptions(["all"]);
      if (areaFilter !== "all") setAreaFilter("all");
      return;
    }
    let relevantCourses =
      ministerioFilter === "all"
        ? cursosData
        : cursosData.filter((c) => c["Ministerio"] === ministerioFilter);
    const areasSet = new Set(
      relevantCourses.map((c) => c["Area"]).filter(Boolean),
    );
    const newAreaOptions = ["all", ...Array.from(areasSet).sort((a, b) => a.localeCompare(b))];
    setAreaOptions(newAreaOptions);
    if (ministerioFilter !== "all" && !newAreaOptions.includes(areaFilter)) {
      setAreaFilter("all");
    }
  }, [ministerioFilter, cursosData, loading, areaFilter]);

  useEffect(() => {
    if (loading && cursosData.length === 0) return;
    let data = [...cursosData];
    const today = dayjs().startOf("day");
    // Filtro por año
    if (yearFilter) {
      data = data.filter((c) => {
        const parsedDate = parseDate(c["Fecha inicio del curso"]);
        return parsedDate && parsedDate.year() === yearFilter;
      });
    }
    if (ministerioFilter !== "all")
      data = data.filter((c) => c["Ministerio"] === ministerioFilter);
    if (areaFilter !== "all")
      data = data.filter((c) => c["Area"] === areaFilter);
    if (nombreFilter.trim()) {
      const term = nombreFilter.trim().toLowerCase();
      data = data.filter((c) =>
        c["Nombre del curso"]?.toLowerCase().includes(term),
      );
    }
    if (monthFilter !== "all") {
      const targetMonth = parseInt(monthFilter, 10);
      data = data.filter((c) => {
        const parsedDate = parseDate(c["Fecha inicio del curso"]);
        return parsedDate && parsedDate.month() === targetMonth;
      });
    }
    if (activosFilterActive) {
      data = data.filter((c) => {
        const startDate = parseDate(c["Fecha inicio del curso"]);
        const endDate = parseDate(c["Fecha fin del curso"]);
        return (
          startDate &&
          endDate &&
          startDate.isSameOrBefore(today) &&
          endDate.isSameOrAfter(today)
        );
      });
    }
    if (omitirCancelados) {
      data = data.filter((c) => c["Estado de Instancia"] !== "CANC");
    }
    setFilteredData(data);
  }, [
    cursosData,
    yearFilter,
    ministerioFilter,
    areaFilter,
    nombreFilter,
    monthFilter,
    activosFilterActive,
    omitirCancelados,
    loading,
  ]);

  const handleRowClick = useCallback((params) => {
    setSelectedRowData(params.row);
    setModalOpen(true);
  }, []);
  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedRowData(null);
  }, []);
  const handleDescargarExcel = useCallback(async () => {
    if (!filteredData.length) return;
    try {
      // Mapeamos los datos filtrados usando la info cruda (_raw) para incluir TODAS las columnas
      let dataToProcess = [...filteredData];
      dataToProcess.sort((a, b) => {
        const dateA = a._raw?.fecha_inicio_curso || '';
        const dateB = b._raw?.fecha_inicio_curso || '';
        return dateA.localeCompare(dateB);
      });

      const dataParaExcel = dataToProcess.map((item) => {
        const raw = item._raw || {};
        const det = raw.detalle_curso || {};
        const asig = raw.detalle_asignado || {};
        const pers = asig.detalle_persona || {};
        const rol = asig.detalle_rol || {};
        const areaAsig = asig.detalle_area || {};

        const formatDate = (dateStr) => dateStr && dayjs(dateStr).isValid() ? dayjs(dateStr).format('DD/MM/YYYY') : (dateStr || "");

        return {
          Curso: raw.curso,
          "Nombre del curso": det.nombre,
          "Area del curso": raw.detalle_curso?.detalle_area?.nombre || "",
          Ministerio:
            raw.detalle_curso?.detalle_area?.detalle_ministerio?.nombre || "",
          "Fecha inicio curso": formatDate(raw.fecha_inicio_curso),
          "Fecha fin curso": formatDate(raw.fecha_fin_curso),
          "Fecha inicio inscripción": formatDate(raw.fecha_inicio_inscripcion),
          "Fecha fin inscripción": formatDate(raw.fecha_fin_inscripcion),
          "Estado Instancia": raw.estado_instancia,
          Cupo: raw.cupo,
          Inscriptos: raw.cantidad_inscriptos || 0,
          Certificados: raw.cantidad_certificados || 0,
          "Cantidad de horas": raw.cantidad_horas,
          "Publica en Portal": formatBooleanToSiNo(
            raw.es_publicada_portal_cc ?? det.publica_pcc,
          ),
          "Es Autogestionado": formatBooleanToSiNo(
            raw.es_autogestionado ?? det.es_autogestionado,
          ),
          "Medio de inscripción": raw.medio_inscripcion,
          "Plataforma de dictado": raw.plataforma_dictado,
          "Tipo de capacitación": raw.tipo_capacitacion,
          "Tiene correlatividad": formatBooleanToSiNo(raw.tiene_correlatividad),
          "Tiene restricción edad": formatBooleanToSiNo(
            raw.tiene_restriccion_edad ?? det.tiene_restriccion_edad,
          ),
          "Restricción Edad Desde": raw.restriccion_edad_desde,
          "Restricción Edad Hasta": raw.restriccion_edad_hasta,
          Comentario: raw.comentario,
          // Datos del Asignado (Coordinador/Responsable)
          "Asignado CUIL": asig.cuil,
          "Asignado Nombre": pers.nombre
            ? `${pers.nombre} ${pers.apellido}`
            : "",
          "Asignado Mail": pers.mail,
          "Asignado Rol": rol.nombre,
          "Asignado Area": areaAsig.nombre,
        };
      });

      // Definimos las columnas que queremos mostrar en el Excel (el orden importa)
      const COLUMNAS_EXCEL = [
        "Curso",
        "Nombre del curso",
        "Area del curso",
        "Ministerio",
        "Fecha inicio curso",
        "Fecha fin curso",
        "Fecha inicio inscripción",
        "Fecha fin inscripción",
        "Estado Instancia",
        "Cupo",
        "Inscriptos",
        "Certificados",
        "Cantidad de horas",
        "Publica en Portal",
        "Es Autogestionado",
        "Medio de inscripción",
        "Plataforma de dictado",
        "Tipo de capacitación",
        "Tiene correlatividad",
        "Tiene restricción edad",
        "Restricción Edad Desde",
        "Restricción Edad Hasta",
        "Comentario",
        "Asignado CUIL",
        "Asignado Nombre",
        "Asignado Mail",
        "Asignado Rol",
        "Asignado Area",
      ];

      await descargarExcel(
        dataParaExcel,
        COLUMNAS_EXCEL,
        "Cronograma_Completo",
      );
    } catch (e) {
      setError("Error al generar el archivo Excel.");
      console.error(e);
    }
  }, [filteredData]);

  const handleDescargarExcelConModal = useCallback(
    async ({ plataforma, incluirCancelados }) => {
      if (!filteredData.length) return;
      try {
        let dataToProcess = filteredData;

        if (plataforma !== "ALL") {
          dataToProcess = dataToProcess.filter((item) => {
            const plat = String(item._raw?.plataforma_dictado || "")
              .toUpperCase()
              .trim();
            if (plataforma === "EXT") return plat === "EXT";
            return plat !== "EXT";
          });
        }

        if (!incluirCancelados) {
          dataToProcess = dataToProcess.filter((item) => {
            const estado = String(item._raw?.estado_instancia || "")
              .toUpperCase()
              .trim();
            return estado !== "CANC";
          });
        }

        if (!dataToProcess.length) {
          setError("No hay datos para exportar con los filtros seleccionados.");
          return;
        }

        dataToProcess.sort((a, b) => {
          const dateA = a._raw?.fecha_inicio_curso || '';
          const dateB = b._raw?.fecha_inicio_curso || '';
          return dateA.localeCompare(dateB);
        });

        const dataParaExcel = dataToProcess.map((item) => {
          const raw = item._raw || {};
          const det = raw.detalle_curso || {};
          const asig = raw.detalle_asignado || {};
          const pers = asig.detalle_persona || {};
          const rol = asig.detalle_rol || {};
          const areaAsig = asig.detalle_area || {};

          const formatDate = (dateStr) => dateStr && dayjs(dateStr).isValid() ? dayjs(dateStr).format('DD/MM/YYYY') : (dateStr || "");

          return {
            Curso: raw.curso,
            "Nombre del curso": det.nombre,
            "Area del curso": raw.detalle_curso?.detalle_area?.nombre || "",
            Ministerio:
              raw.detalle_curso?.detalle_area?.detalle_ministerio?.nombre || "",
            "Fecha inicio curso": formatDate(raw.fecha_inicio_curso),
            "Fecha fin curso": formatDate(raw.fecha_fin_curso),
            "Fecha inicio inscripción": formatDate(raw.fecha_inicio_inscripcion),
            "Fecha fin inscripción": formatDate(raw.fecha_fin_inscripcion),
            "Estado Instancia": raw.estado_instancia,
            Cupo: raw.cupo,
            Inscriptos: raw.cantidad_inscriptos || 0,
            Certificados: raw.cantidad_certificados || 0,
            "Cantidad de horas": raw.cantidad_horas,
            "Publica en Portal": formatBooleanToSiNo(
              raw.es_publicada_portal_cc ?? det.publica_pcc,
            ),
            "Es Autogestionado": formatBooleanToSiNo(
              raw.es_autogestionado ?? det.es_autogestionado,
            ),
            "Medio de inscripción": raw.medio_inscripcion,
            "Plataforma de dictado": raw.plataforma_dictado,
            "Tipo de capacitación": raw.tipo_capacitacion,
            "Tiene correlatividad": formatBooleanToSiNo(
              raw.tiene_correlatividad,
            ),
            "Tiene restricción edad": formatBooleanToSiNo(
              raw.tiene_restriccion_edad ?? det.tiene_restriccion_edad,
            ),
            "Restricción Edad Desde": raw.restriccion_edad_desde,
            "Restricción Edad Hasta": raw.restriccion_edad_hasta,
            Comentario: raw.comentario,
            "Asignado CUIL": asig.cuil,
            "Asignado Nombre": pers.nombre
              ? `${pers.nombre} ${pers.apellido}`
              : "",
            "Asignado Mail": pers.mail,
            "Asignado Rol": rol.nombre,
            "Asignado Area": areaAsig.nombre,
          };
        });

        const COLUMNAS_EXCEL = [
          "Curso",
          "Nombre del curso",
          "Area del curso",
          "Ministerio",
          "Fecha inicio curso",
          "Fecha fin curso",
          "Fecha inicio inscripción",
          "Fecha fin inscripción",
          "Estado Instancia",
          "Cupo",
          "Inscriptos",
          "Certificados",
          "Cantidad de horas",
          "Publica en Portal",
          "Es Autogestionado",
          "Medio de inscripción",
          "Plataforma de dictado",
          "Tipo de capacitación",
          "Tiene correlatividad",
          "Tiene restricción edad",
          "Restricción Edad Desde",
          "Restricción Edad Hasta",
          "Comentario",
          "Asignado CUIL",
          "Asignado Nombre",
          "Asignado Mail",
          "Asignado Rol",
          "Asignado Area",
        ];

        await descargarExcel(
          dataParaExcel,
          COLUMNAS_EXCEL,
          "Cronograma_Completo",
        );
        setExcelModalOpen(false);
      } catch (e) {
        setError("Error al generar el archivo Excel.");
        console.error(e);
      }
    },
    [filteredData],
  );

  const handleClickDescargar = useCallback(() => {
    const userRol = user?.rol;
    if (userRol === "GA" || userRol === "ADM" || userRol === "ROOT") {
      setExcelModalOpen(true);
    } else {
      // REF u otros roles: descarga directa sin modal
      handleDescargarExcel();
    }
  }, [user, handleDescargarExcel]);
  const handleMinisterioChange = useCallback(
    (e) => setMinisterioFilter(e.target.value),
    [],
  );
  const handleAreaChange = useCallback(
    (e) => setAreaFilter(e.target.value),
    [],
  );
  const handleNombreChange = useCallback(
    (e) => setNombreFilter(e.target.value),
    [],
  );
  const handleYearChange = useCallback(
    (e) => setYearFilter(e.target.value),
    [],
  );
  const handleMonthChange = useCallback(
    (e) => setMonthFilter(e.target.value),
    [],
  );
  const handleToggleActivosFilter = useCallback(
    () => setActivosFilterActive((prev) => !prev),
    [],
  );
  const handleToggleOmitirCancelados = useCallback(
    () => setOmitirCancelados((prev) => !prev),
    [],
  );
  const handleClearFilters = useCallback(() => {
    setNombreFilter("");
    setMinisterioFilter("all");
    setAreaFilter("all");
    setYearFilter(new Date().getFullYear());
    setMonthFilter("all");
    setActivosFilterActive(false);
    setOmitirCancelados(false);
  }, []);
  const isFilterActive = useMemo(
    () =>
      nombreFilter.trim() !== "" ||
      ministerioFilter !== "all" ||
      areaFilter !== "all" ||
      yearFilter !== new Date().getFullYear() ||
      monthFilter !== "all" ||
      activosFilterActive ||
      omitirCancelados,
    [
      nombreFilter,
      ministerioFilter,
      areaFilter,
      yearFilter,
      monthFilter,
      activosFilterActive,
      omitirCancelados,
    ],
  );

  const getRowClassName = useCallback((params) => {
    const estado = params.row["Estado de Instancia"];
    if (estado === "CANC") return "row-cancelado";
    return "";
  }, []);

  const renderDetailItem = useCallback(
    (label, value, isBoolean = false) => {
      let displayValue;
      if (isBoolean) {
        displayValue = formatBooleanToSiNo(value);
      } else if (
        label.toLowerCase().includes("inicio") ||
        label.toLowerCase().includes("fin") ||
        label.toLowerCase().includes("fecha")
      ) {
        // Intentamos parsear como fecha si el label sugiere que lo es
        const d = dayjs(value);
        displayValue =
          value && d.isValid()
            ? d.format("DD/MM/YYYY")
            : formatValue(value) || "-";
      } else {
        displayValue = formatValue(value) || "-";
      }

      if (displayValue === "" || displayValue === "-" || value === null)
        return null;

      return (
        <React.Fragment key={label}>
          <ListItem sx={{ py: 0.8, px: 0 }}>
            <ListItemText
              primary={displayValue}
              secondary={label}
              primaryTypographyProps={{
                fontWeight: 500,
                color: "text.primary",
                wordBreak: "break-word",
              }}
              secondaryTypographyProps={{
                fontSize: "0.8rem",
                color: "text.secondary",
              }}
            />
          </ListItem>
          <Divider component="li" sx={{ my: 0.5 }} />
        </React.Fragment>
      );
    },
    [formatValue],
  );

  // Eliminado el bloqueo total por Backdrop
  if (error)
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error al cargar datos: {error}</Alert>
      </Box>
    );
  if (!loading && !cursosData.length)
    return (
      <Box sx={{ p: 3 }}>
        <Typography>No se encontraron datos en el cronograma.</Typography>
      </Box>
    );

  return (
    <>
      <div style={gridContainerStyle}>
        <div>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Titulo texto="Cronograma" />
            {tabValue === 0 && (
              <BotonCircular
                icon="descargar"
                onClick={handleClickDescargar}
                tooltip="Descargar Vista Actual"
                disabled={loading || !filteredData.length}
              />
            )}
          </Box>
        </div>

        <div>
          <Divider sx={{ mb: 1, borderBottomWidth: 2 }} />
        </div>

        <div>
          <Paper
            elevation={1}
            sx={{
              p: 2,
              width: "100%",
              opacity: loading ? 0.6 : 1,
              transition: 'opacity 0.3s ease',
              pointerEvents: loading ? 'none' : 'auto'
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2.5}>
                <TextField
                  fullWidth
                  label="Buscar por Nombre"
                  variant="outlined"
                  size="small"
                  value={nombreFilter}
                  onChange={handleNombreChange}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  disabled={loading || ministerioOptions.length <= 1}
                >
                  <InputLabel>Ministerio</InputLabel>
                  <Select
                    value={ministerioFilter}
                    label="Ministerio"
                    onChange={handleMinisterioChange}
                    sx={{
                      '& .MuiSelect-select': {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap'
                      }
                    }}
                  >
                    <MenuItem value="all">
                      <em>Todos</em>
                    </MenuItem>
                    {ministerioOptions
                      .filter((opt) => opt !== "all")
                      .map((opt, i) => (
                        <MenuItem 
                          key={i} 
                          value={opt}
                          sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                        >
                          {opt}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={1.5}>
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  disabled={loading || areaOptions.length <= 1}
                >
                  <InputLabel>Área</InputLabel>
                  <Select
                    value={areaFilter}
                    label="Área"
                    onChange={handleAreaChange}
                    sx={{
                      '& .MuiSelect-select': {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap'
                      }
                    }}
                  >
                    <MenuItem value="all">
                      <em>Todas</em>
                    </MenuItem>
                    {areaOptions
                      .filter((opt) => opt !== "all")
                      .map((opt, i) => (
                        <MenuItem 
                          key={i} 
                          value={opt}
                          sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                        >
                          {opt}
                        </MenuItem>
                      ))}
                    {ministerioFilter !== "all" && areaOptions.length <= 1 && (
                      <MenuItem value="all" disabled sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <em>(Sin áreas)</em>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  disabled={loading || isREF}
                >
                  <InputLabel>Año</InputLabel>
                  <Select
                    value={yearFilter}
                    label="Año"
                    onChange={handleYearChange}
                  >
                    {availableYears.map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small" variant="outlined" disabled={loading}>
                  <InputLabel>Mes Inicio Curso</InputLabel>
                  <Select
                    value={monthFilter}
                    label="Mes Inicio Curso"
                    onChange={handleMonthChange}
                  >
                    <MenuItem value="all">
                      <em>Todos</em>
                    </MenuItem>
                    {MONTH_NAMES.map((m, i) => (
                      <MenuItem key={i} value={i.toString()}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={1.5} sx={{ display: "flex" }}>
                <Tooltip
                  title={
                    activosFilterActive
                      ? "Mostrar todos"
                      : "Mostrar solo activos"
                  }
                >
                  <Button
                    fullWidth
                    variant={activosFilterActive ? "contained" : "outlined"}
                    size="medium"
                    onClick={handleToggleActivosFilter}
                    startIcon={<AccessTimeIcon />}
                    sx={{ height: "40px" }}
                  >
                    Activos
                  </Button>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={6} md={1.5} sx={{ display: "flex" }}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="medium"
                  onClick={handleClearFilters}
                  disabled={!isFilterActive}
                  startIcon={<ClearAllIcon />}
                  sx={{ height: "40px" }}
                >
                  Limpiar
                </Button>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                md={12}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={omitirCancelados}
                      onChange={handleToggleOmitirCancelados}
                      color="secondary"
                      size="small"
                    />
                  }
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <BlockIcon
                        fontSize="small"
                        color={omitirCancelados ? "secondary" : "action"}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: omitirCancelados ? 600 : 400,
                          color: omitirCancelados
                            ? "secondary.main"
                            : "text.secondary",
                        }}
                      >
                        Omitir Cancelados
                      </Typography>
                    </Box>
                  }
                  sx={{ mr: 0 }}
                />
              </Grid>
            </Grid>
          </Paper>
        </div>

        <div>
          {/* Tabs */}
          <Paper
            elevation={0}
            sx={{
              p: 0.5,
              backgroundColor: "#f5f5f5",
              borderRadius: "999px",
              display: "inline-flex",
              alignItems: "center",
              border: "1px solid rgba(0,0,0,0.06)",
              mb: 2,
            }}
          >
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              TabIndicatorProps={{ style: { display: "none" } }}
              sx={{
                minHeight: "auto",
                "& .MuiTabs-flexContainer": {
                  gap: 0.5,
                },
              }}
            >
              <Tab
                label="Tabla"
                sx={{
                  textTransform: "none",
                  minHeight: 34,
                  borderRadius: "999px",
                  px: 2.5,
                  fontWeight: 500,
                  color: "text.secondary",
                  transition: "all 0.2s ease",
                  "&.Mui-selected": {
                    backgroundColor: "#fff",
                    color: "text.primary",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.7)",
                  },
                }}
              />

              <Tab
                label="Calendario"
                sx={{
                  textTransform: "none",
                  minHeight: 34,
                  borderRadius: "999px",
                  px: 2.5,
                  fontWeight: 500,
                  color: "text.secondary",
                  transition: "all 0.2s ease",
                  "&.Mui-selected": {
                    backgroundColor: "#fff",
                    color: "text.primary",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.7)",
                  },
                }}
              />
            </Tabs>
          </Paper>

          {/* Content del Tab */}
          <Box sx={{ display: tabValue === 0 ? "block" : "none" }}>
            {tabValue === 0 && (
              <>
                {loading && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      my: 4,
                      alignItems: "center",
                      minHeight: "400px"
                    }}
                  >
                    <BurbujasLoader />
                  </Box>
                )}

                {(!loading || cursosData.length > 0) && (
                  <Paper elevation={3} sx={{ height: 600, width: "100%", opacity: loading ? 0.6 : 1 }}>
                    <DataGrid
                      rows={filteredData}
                      columns={columnsForGrid}
                      onRowClick={handleRowClick}
                      getRowId={(r) => r.id}
                      loading={loading}
                      density="compact"
                      disableRowSelectionOnClick
                      getRowClassName={getRowClassName}
                      initialState={{
                        sorting: {
                          sortModel: [
                            { field: "Fecha inicio del curso", sort: "asc" },
                          ],
                        },
                      }}
                      sx={{
                        "& .row-cancelado": {
                          backgroundColor: "rgba(211, 47, 47, 0.08)",
                          borderLeft: "4px solid #d32f2f",
                          color: "#b71c1c",
                          "&:hover": {
                            backgroundColor: "rgba(211, 47, 47, 0.15)",
                          },
                          "& .MuiDataGrid-cell": {
                            color: "#b71c1c",
                          },
                        },
                      }}
                    />
                  </Paper>
                )}
              </>
            )}
          </Box>

          <Box sx={{ display: tabValue === 1 ? "block" : "none" }}>
            {tabValue === 1 && (
              <>
                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 4, minHeight: '400px', alignItems: 'center' }}>
                    <BurbujasLoader />
                  </Box>
                )}
                {(!loading || cursosData.length > 0) && (
                  <Box sx={{ opacity: loading ? 0.6 : 1 }}>
                    <CronogramaCalendar
                      filteredData={filteredData}
                      parseDate={parseDate}
                      yearFilter={yearFilter}
                      monthFilter={monthFilter}
                      setYearFilter={setYearFilter}
                      setMonthFilter={setMonthFilter}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </div>
      </div>

      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="course-detail-title"
      >
        <Box sx={modalStyle}>
          {selectedRowData && (
            <Card
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                overflow: "hidden",
              }}
            >
              <CardHeader
                avatar={<InfoIcon color="primary" />}
                id="course-detail-title"
                title={selectedRowData["Nombre del curso"]}
                subheader={`Código: ${selectedRowData["Código del curso"]}`}
                titleTypographyProps={{
                  variant: "h6",
                  fontWeight: "bold",
                  color: "primary.main",
                }}
                action={
                  <IconButton onClick={handleCloseModal}>
                    <CloseIcon />
                  </IconButton>
                }
                sx={{
                  bgcolor: "grey.100",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              />
              <CardContent sx={{ overflowY: "auto", flexGrow: 1, p: 2 }}>
                <List dense>
                  <Divider sx={{ mb: 1 }}>
                    <Chip label="Detalles del Curso" size="small" />
                  </Divider>
                  {renderDetailItem(
                    "Ministerio",
                    selectedRowData["Ministerio"],
                  )}
                  {renderDetailItem("Área", selectedRowData["Area"])}
                  {renderDetailItem(
                    "Tipo de capacitación",
                    selectedRowData["Tipo de capacitación"],
                  )}
                  {renderDetailItem(
                    "Cantidad de horas",
                    selectedRowData["Cantidad de horas"],
                  )}

                  <Divider sx={{ my: 2 }}>
                    <Chip label="Detalles de la Instancia" size="small" />
                  </Divider>
                  {renderDetailItem(
                    "Estado",
                    selectedRowData["Estado de Instancia"],
                  )}
                  {renderDetailItem("Cupo", selectedRowData["Cupo"])}
                  {renderDetailItem(
                    "Inscriptos",
                    selectedRowData["Cantidad de inscriptos"],
                  )}
                  {renderDetailItem(
                    "Medio de inscripción",
                    selectedRowData["Medio de inscripción"],
                  )}
                  {renderDetailItem(
                    "Plataforma de dictado",
                    selectedRowData["Plataforma de dictado"],
                  )}
                  {renderDetailItem(
                    "Comentario",
                    selectedRowData["Comentario"],
                  )}

                  <Divider sx={{ my: 2 }}>
                    <Chip label="Fechas" size="small" />
                  </Divider>
                  {renderDetailItem(
                    "Inicio de Inscripción",
                    selectedRowData["Fecha inicio de inscripción"],
                  )}
                  {renderDetailItem(
                    "Fin de Inscripción",
                    selectedRowData["Fecha fin de inscripción"],
                  )}
                  {renderDetailItem(
                    "Inicio del Curso",
                    selectedRowData["Fecha inicio del curso"],
                  )}
                  {renderDetailItem(
                    "Fin del Curso",
                    selectedRowData["Fecha fin del curso"],
                  )}

                  <Divider sx={{ my: 2 }}>
                    <Chip label="Configuración y Restricciones" size="small" />
                  </Divider>
                  {renderDetailItem(
                    "Es Autogestionado",
                    selectedRowData["Es Autogestionado"],
                    true,
                  )}
                  {renderDetailItem(
                    "Publicada en Portal",
                    selectedRowData["Publica PCC"],
                    true,
                  )}

                  <ListItem sx={{ py: 0.8, px: 0 }}>
                    <ListItemText
                      primary={
                        selectedRowData["Tiene restricción por edad"]
                          ? `Desde ${selectedRowData["Edad Desde"]} hasta ${selectedRowData["Edad Hasta"] || "sin límite"} años`
                          : "Ninguna"
                      }
                      secondary="Restricción por Edad"
                      primaryTypographyProps={{
                        fontWeight: 500,
                        color: "text.primary",
                        wordBreak: "break-word",
                      }}
                      secondaryTypographyProps={{
                        fontSize: "0.8rem",
                        color: "text.secondary",
                      }}
                    />
                  </ListItem>
                  <Divider component="li" sx={{ my: 0.5 }} />

                  <ListItem sx={{ py: 0.8, px: 0 }}>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            mt: 0.5,
                          }}
                        >
                          {selectedRowData["Restricciones por Departamento"]
                            .length > 0 ? (
                            selectedRowData[
                              "Restricciones por Departamento"
                            ].map((d) => (
                              <Chip
                                key={d.departamento_id}
                                label={
                                  d.detalle_departamento?.nombre ||
                                  d.departamento_id
                                }
                                size="small"
                              />
                            ))
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.primary"
                              sx={{ fontWeight: 500 }}
                            >
                              Ninguna
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary="Restricción por Departamento"
                      secondaryTypographyProps={{
                        fontSize: "0.8rem",
                        color: "text.secondary",
                      }}
                    />
                  </ListItem>
                  <Divider component="li" sx={{ my: 0.5 }} />

                  <ListItem sx={{ py: 0.8, px: 0 }}>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            mt: 0.5,
                            mb: 0.5,
                          }}
                        >
                          {selectedRowData["Restricciones por Correlatividad"]
                            .length > 0 ? (
                            selectedRowData[
                              "Restricciones por Correlatividad"
                            ].map((c) => (
                              <Chip
                                key={c.curso_correlativo}
                                label={
                                  c.detalle_curso_correlativo?.nombre ||
                                  c.curso_correlativo
                                }
                                size="small"
                              />
                            ))
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.primary"
                              sx={{ fontWeight: 500 }}
                            >
                              Ninguna
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary="Restricción por Correlatividad"
                      secondaryTypographyProps={{
                        fontSize: "0.8rem",
                        color: "text.secondary",
                      }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          )}
        </Box>
      </Modal>

      <ExcelDownloadModal
        open={excelModalOpen}
        onClose={() => setExcelModalOpen(false)}
        onDownload={handleDescargarExcelConModal}
        infoMessage="Los filtros activos en la pantalla ya están siendo aplicados a esta descarga. El excel se encuentra ordenado por fecha de inicio de curso."
      />
    </>
  );
};

export default Cronograma;
